import { useCallback } from 'react';
import axios from 'axios';

export const useAdminActions = ({
  BACKEND_URL,
  user,
  adminPasswordDrafts,
  setAdminPasswordDrafts,
  adminReviewNotes,
  setAdminReviews,
  setAdminUsers,
  adminEventType,
  adminEventId,
  setAdminEventId,
  adminMessageBarbershop,
  adminMessageDraft,
  setAdminMessageDraft,
  setAdminMessageBarbershop,
  setAdminEditBarbershop,
  setAdminSelectedPlace,
  setAdminEditBarbershopDraft,
  setAdminBarbershops,
  setAdminNotifications,
  maintenanceEnabled,
  maintenanceMessage,
  setMaintenanceBanner,
  adminBlockDrafts,
  adminSearchQuery,
  setAdminSearchResults,
  requestConfirmation,
  showNotification,
  uploadImageToImageKit,
  setAdminImageUploading
}) => {
  const handleAdminUpdateUser = useCallback(async (userId, payload) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/admin/users/${userId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = response.data.user;
      setAdminUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      showNotification('Usuario actualizado', 'Los cambios fueron guardados', 'success');
    } catch (error) {
      console.error('Error updating admin user:', error);
      showNotification('Error', 'No se pudo actualizar el usuario', 'destructive');
    }
  }, [BACKEND_URL, setAdminUsers, showNotification]);

  const handleAdminResetPassword = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const password = adminPasswordDrafts[userId];
      if (!password) {
        showNotification('Error', 'Ingresa una nueva contraseña', 'destructive');
        return;
      }
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/password`, { password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminPasswordDrafts((prev) => ({ ...prev, [userId]: '' }));
      showNotification('Contraseña actualizada', 'Se guardó la nueva contraseña', 'success');
    } catch (error) {
      console.error('Error resetting password:', error);
      showNotification('Error', 'No se pudo actualizar la contraseña', 'destructive');
    }
  }, [BACKEND_URL, adminPasswordDrafts, setAdminPasswordDrafts, showNotification]);

  const handleAdminDeleteReview = useCallback(async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminReviews((prev) => prev.filter((r) => r.id !== reviewId));
      showNotification('Reseña eliminada', 'Se eliminó la reseña', 'success');
    } catch (error) {
      console.error('Error deleting review:', error);
      showNotification('Error', 'No se pudo eliminar la reseña', 'destructive');
    }
  }, [BACKEND_URL, setAdminReviews, showNotification]);

  const handleAdminDeleteEvent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!adminEventId) {
        showNotification('Error', 'Ingresa el ID del evento', 'destructive');
        return;
      }
      await axios.delete(`${BACKEND_URL}/api/admin/events/${adminEventType}/${adminEventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminEventId('');
      showNotification('Evento eliminado', 'El evento fue eliminado para todos', 'success');
    } catch (error) {
      console.error('Error deleting event:', error);
      showNotification('Error', 'No se pudo eliminar el evento', 'destructive');
    }
  }, [BACKEND_URL, adminEventId, adminEventType, setAdminEventId, showNotification]);

  const handleAdminFlagReview = useCallback(async (reviewId, flagged) => {
    try {
      const token = localStorage.getItem('token');
      const note = adminReviewNotes[reviewId] || '';
      await axios.put(`${BACKEND_URL}/api/admin/reviews/${reviewId}/flag`, { flagged, note }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminReviews((prev) =>
        prev.map((review) =>
          review.id === reviewId
            ? { ...review, flagged, flag_note: note, flagged_by: flagged ? user?.id : null }
            : review
        )
      );
      showNotification('Reseña actualizada', 'Se actualizó el estado de la reseña', 'success');
    } catch (error) {
      console.error('Error flagging review:', error);
      showNotification('Error', 'No se pudo marcar la reseña', 'destructive');
    }
  }, [BACKEND_URL, adminReviewNotes, setAdminReviews, showNotification, user?.id]);

  const handleAdminSendBarbershopMessage = useCallback(async () => {
    if (!adminMessageBarbershop || !adminMessageDraft.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/barbershops/${adminMessageBarbershop.id}/message`,
        { message: adminMessageDraft.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdminMessageDraft('');
      setAdminMessageBarbershop(null);
      showNotification('Mensaje enviado', 'El barbero recibió tu mensaje', 'success');
    } catch (error) {
      console.error('Error sending barbershop message:', error);
      showNotification('Error', 'No se pudo enviar el mensaje', 'destructive');
    }
  }, [
    BACKEND_URL,
    adminMessageBarbershop,
    adminMessageDraft,
    setAdminMessageDraft,
    setAdminMessageBarbershop,
    showNotification
  ]);

  const handleAdminEditBarbershop = useCallback((barbershop) => {
    setAdminEditBarbershop(barbershop);
    setAdminSelectedPlace(null);
    setAdminEditBarbershopDraft({
      name: barbershop.name || '',
      address: barbershop.address || '',
      city: barbershop.city || '',
      comuna: barbershop.comuna || '',
      region: barbershop.region || '',
      street: barbershop.street || '',
      street_number: barbershop.street_number || '',
      postal_code: barbershop.postal_code || '',
      lat: typeof barbershop.lat === 'number' ? barbershop.lat : null,
      lng: typeof barbershop.lng === 'number' ? barbershop.lng : null,
      phone: barbershop.phone || '',
      description: barbershop.description || '',
      available: barbershop.available !== false,
      services: Array.isArray(barbershop.services) ? barbershop.services : [],
      profile_image: barbershop.profile_image || ''
    });
  }, [setAdminEditBarbershop, setAdminEditBarbershopDraft, setAdminSelectedPlace]);

  const addAdminService = useCallback(() => {
    setAdminEditBarbershopDraft((prev) => ({
      ...prev,
      services: [...(prev.services || []), { name: '', price: 0, duration: 30 }]
    }));
  }, [setAdminEditBarbershopDraft]);

  const updateAdminService = useCallback((index, field, value) => {
    setAdminEditBarbershopDraft((prev) => {
      const updated = [...(prev.services || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, services: updated };
    });
  }, [setAdminEditBarbershopDraft]);

  const removeAdminService = useCallback((index) => {
    setAdminEditBarbershopDraft((prev) => ({
      ...prev,
      services: (prev.services || []).filter((_, i) => i !== index)
    }));
  }, [setAdminEditBarbershopDraft]);

  const handleAdminUploadBarbershopImage = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showNotification('Archivo inválido', 'El archivo debe ser una imagen.', 'destructive');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Archivo inválido', 'La imagen debe ser menor a 5MB.', 'destructive');
      return;
    }
    try {
      setAdminImageUploading(true);
      const imageUrl = await uploadImageToImageKit(file, '/barbershops');
      if (!imageUrl) {
        showNotification('Error al subir', 'No se pudo subir la imagen. Revisa ImageKit.', 'destructive');
        return;
      }
      setAdminEditBarbershopDraft((prev) => ({ ...prev, profile_image: imageUrl }));
      showNotification('Imagen actualizada', 'La foto se guardará al confirmar.', 'success');
    } catch (error) {
      console.error('Error uploading admin barbershop image:', error);
      showNotification('Error', 'No se pudo subir la imagen.', 'destructive');
    } finally {
      setAdminImageUploading(false);
      if (event.target) event.target.value = '';
    }
  }, [setAdminEditBarbershopDraft, setAdminImageUploading, showNotification, uploadImageToImageKit]);

  const submitAdminBarbershopUpdate = useCallback(async (adminEditBarbershop, adminEditBarbershopDraft) => {
    if (!adminEditBarbershop) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/barbershops/${adminEditBarbershop.id}`,
        adminEditBarbershopDraft,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = response.data.barbershop;
      setAdminBarbershops((prev) =>
        prev.map((shop) => (shop.id === updated.id ? updated : shop))
      );
      setAdminEditBarbershop(null);
      showNotification('Barbería actualizada', 'Se guardaron los cambios', 'success');
    } catch (error) {
      console.error('Error updating barbershop:', error);
      showNotification('Error', 'No se pudo actualizar la barbería', 'destructive');
    }
  }, [BACKEND_URL, setAdminBarbershops, setAdminEditBarbershop, showNotification]);

  const handleAdminDeleteBarbershop = useCallback(async (barbershopId) => {
    const confirmed = await requestConfirmation({
      title: 'Eliminar barbería',
      description: '¿Seguro que deseas eliminar esta barbería? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/barbershops/${barbershopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminBarbershops((prev) => prev.filter((shop) => shop.id !== barbershopId));
      showNotification('Barbería eliminada', 'Se eliminó correctamente', 'success');
    } catch (error) {
      console.error('Error deleting barbershop:', error);
      showNotification('Error', 'No se pudo eliminar la barbería', 'destructive');
    }
  }, [BACKEND_URL, requestConfirmation, setAdminBarbershops, showNotification]);

  const markAdminNotificationRead = useCallback(async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotifications((prev) =>
        prev.map((note) => (note.id === notificationId ? { ...note, read: true } : note))
      );
    } catch (error) {
      console.error('Error updating notification:', error);
    }
  }, [BACKEND_URL, setAdminNotifications]);

  const handleAdminSearch = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!adminSearchQuery) {
        setAdminSearchResults({ users: [], barbershops: [], reviews: [] });
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/admin/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: adminSearchQuery }
      });
      setAdminSearchResults(response.data);
    } catch (error) {
      console.error('Error searching admin:', error);
    }
  }, [BACKEND_URL, adminSearchQuery, setAdminSearchResults]);

  const updateAdminMaintenance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/settings/maintenance`, {
        enabled: maintenanceEnabled,
        message: maintenanceMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Mantenimiento actualizado', 'Se guardó la configuración', 'success');
      setMaintenanceBanner({ enabled: maintenanceEnabled, message: maintenanceMessage });
    } catch (error) {
      console.error('Error updating maintenance:', error);
      showNotification('Error', 'No se pudo actualizar mantenimiento', 'destructive');
    }
  }, [BACKEND_URL, maintenanceEnabled, maintenanceMessage, setMaintenanceBanner, showNotification]);

  const handleAdminSaveBlock = useCallback(async (userId) => {
    const draft = adminBlockDrafts[userId];
    if (!draft) return;
    await handleAdminUpdateUser(userId, {
      blocked: draft.blocked,
      blocked_until: draft.blocked_until,
      blocked_reason: draft.blocked_reason
    });
  }, [adminBlockDrafts, handleAdminUpdateUser]);

  const handleAdminSetBarberBarbershop = useCallback(async (userId, barbershopId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/barbershop`, { barbershop_id: barbershopId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Barbería asignada', 'El barbero fue asignado a la barbería', 'success');
    } catch (error) {
      console.error('Error setting barber barbershop:', error);
      showNotification('Error', error.response?.data?.detail || 'No se pudo asignar la barbería', 'destructive');
    }
  }, [BACKEND_URL, showNotification]);

  return {
    handleAdminUpdateUser,
    handleAdminResetPassword,
    handleAdminDeleteReview,
    handleAdminDeleteEvent,
    handleAdminFlagReview,
    handleAdminSendBarbershopMessage,
    handleAdminEditBarbershop,
    addAdminService,
    updateAdminService,
    removeAdminService,
    handleAdminUploadBarbershopImage,
    submitAdminBarbershopUpdate,
    handleAdminDeleteBarbershop,
    markAdminNotificationRead,
    handleAdminSearch,
    updateAdminMaintenance,
    handleAdminSaveBlock,
    handleAdminSetBarberBarbershop
  };
};
